import { Box, TableCell } from "@mui/material";
import { useEffect, useState } from "react";
import { Company, PaginatedListCompany } from "../../services/swagger";
import { CompanyService } from "../../services/api";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import DataView, { QueryParameters } from "../organisms/DataView";
import ItemCard from "../organisms/ItemCard";
import { useMutation, useQuery } from "@tanstack/react-query";
import CreateCompanyDialog from "../organisms/Company/CreateCompanyDialog";
import useRefresh from "../../hooks/useRefresh";
import EditCompanyDialog from "../organisms/Company/EditCompanyDialog";

export default function CompaniesPage() {
  const { t } = useTranslation("Company");
  const { _id } = useParams();
  const navigate = useNavigate();

  const [createDialog, setCreateDialog] = useState(false);
  const [openedCompany, setOpenedCompany] = useState<Company>();
  const [query, setQuery] = useState<QueryParameters>();

  const { mutate: loadCompany } = useMutation({
    mutationFn: async () => {
      if (!_id) return;
      const res = await CompanyService.getCompany({ id: _id });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) setOpenedCompany(data);
    },
  });

  const loadCompanies = async (): Promise<PaginatedListCompany | null> => {
    if (!query) return null;
    const res = await CompanyService.getCompanyList({
      search: query.search,
      limit: query.limit,
      skip: query.skip,
      filters: query.filters,
      sortDirection: query.sortDirection,
      sortField: query.sortField,
    });
    if (res?.data?.data) return res.data.data;
    else return null;
  };

  const {
    data: companies,
    isPlaceholderData: isLoading,
    refetch,
  } = useQuery({
    queryFn: loadCompanies,
    queryKey: ["companies", { query }],
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  // const navigateToCompany = (company: Company) => {
  //   navigate(`/m/business/companies/${company._id}`);
  // };

  const openCompany = (company: Company) => {
    navigate({
      pathname: `/m/business/companies/${company._id}`,
      search: window.location.search,
    });
  };

  const handleDataTableChange = (query: QueryParameters) => {
    setQuery(query);
  };

  const handleCloseCompany = () => {
    if (_id) navigate(-1);
    setOpenedCompany(undefined);
  };

  useEffect(() => {
    if (_id) loadCompany();
  }, [_id]);

  useRefresh(refetch);

  return (
    <Box height={1}>
      <CreateCompanyDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
      />
      <EditCompanyDialog company={openedCompany} onClose={handleCloseCompany} />
      <DataView
        name={t("Companies")}
        loading={isLoading || !companies}
        onItemClick={(item) => openCompany(item)}
        headers={[
          { field: "name", name: t("Name"), type: "string" },
          { field: "regNo", name: t("RegNo"), type: "string" },
          {
            field: "emails",
            name: t("Emails"),
            type: "other",
            renderRowCell: (row: Company) => (
              <TableCell
                sx={{
                  maxWidth: 70,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.emails.length ? row.emails.join(", ") : "—"}
              </TableCell>
            ),
          },
          {
            field: "phones",
            name: t("Phones"),
            type: "other",
            renderRowCell: (row: Company) => (
              <TableCell
                sx={{
                  minWidth: 100,
                  maxWidth: 150,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.phones.join(",") || "—"}
              </TableCell>
            ),
          },
          { field: "extRef", name: t("ExtRef"), type: "string" },
        ]}
        onChange={handleDataTableChange}
        items={companies?.docs || []}
        count={companies?.count || 0}
        renderItem={(company) => (
          <ItemCard company={company} onClick={() => openCompany(company)} />
        )}
        pagination
        queryFunction={CompanyService.getCompanyListQuery}
        button={{
          text: t("NewCompany"),
          onClick: () => setCreateDialog(true),
        }}
      />
    </Box>
  );
}
